import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { api } from '../lib/api';
import { toast } from 'react-toastify';
import { useCartStore } from '../store/cartStore';
import Lightbox from 'yet-another-react-lightbox';
import 'yet-another-react-lightbox/styles.css';
import ListingSEO from '../components/ListingSEO';
import DescriptionSection from '../components/DescriptionSection';
import AvailabilitySection from '../components/AvailabilitySection';
import BookingDetailsSection from '../components/BookingDetailsSection';
import { dateOnlyFieldToLocalMidnight } from '../utils/timezone';

interface RenterDetailRequirement {
  fieldName: string;
  description: string;
  required: boolean;
}

interface Photo {
  id: string;
  s3Key: string;
  order: number;
  isPrimary: boolean;
}

interface Listing {
  _id: string;
  name: string;
  title: string;
  description: string;
  address?: string;
  timezone: string;
  showAddress: string;
  showOnMap: boolean;
  latitude?: number;
  longitude?: number;
  approximateLatitude?: number;
  approximateLongitude?: number;
  supportPhoneNumber?: string;
  showSupportPhone: string;
  renterDetailRequirements: RenterDetailRequirement[];
  photos: Photo[];
  youtubeVideos: string[];
  preferredSlotView: 'list' | 'grid';
  showCalendar: boolean;
  showAvailabilityAboveDescription: boolean;
}

interface ContactInfo {
  address?: string;
  latitude?: number;
  longitude?: number;
  supportPhone?: string;
}

interface TimeSlot {
  windowId: string;
  windowName?: string;
  windowType: 'daily' | 'weekly' | 'monthly';
  startDateTime: string;
  endDateTime: string;
  price: number;
  availableQuantity: number;
  totalQuantity: number;
}

interface RentalWindow {
  _id: string;
  name?: string;
  windowType: 'monthly' | 'weekly' | 'daily';
  startTime?: string;
  endTime?: string;
  intervalMinutes?: number;
  repeatDaysOfWeek?: number[];
  specificDate?: string;
  isRepeating: boolean;
  repeatStartDate?: string;
  repeatEndDate?: string;
  repeatExceptions: string[];
  pricePerInterval: number;
  quantity: number;
}

const PublicListingPage: React.FC = () => {
  const { listingName } = useParams();
  const navigate = useNavigate();
  const [listing, setListing] = useState<Listing | null>(null);
  const [windows, setWindows] = useState<RentalWindow[]>([]);
  const [slots, setSlots] = useState<TimeSlot[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isLoadingDateSlots, setIsLoadingDateSlots] = useState(false);
  const [isLoadingMoreSlots, setIsLoadingMoreSlots] = useState(false);
  const [hasMoreSlots, setHasMoreSlots] = useState(false);
  const [renterDetails, setRenterDetails] = useState<Record<string, string>>({});
  const [isBooking, setIsBooking] = useState(false);
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [lightboxIndex, setLightboxIndex] = useState(0);
  const [revealedContactInfo, setRevealedContactInfo] = useState<ContactInfo | null>(null);
  const [selectedDate, setSelectedDate] = useState<Date | null>(null);
  const [selectedSlots, setSelectedSlots] = useState<Set<string>>(new Set());
  const [view, setView] = useState<'list' | 'grid'>('list');
  const { addItem } = useCartStore();

  const s3BaseUrl = process.env.REACT_APP_S3_BASE_URL || 'https://pay2rentme-images-dev.s3.amazonaws.com';

  const handleContactReveal = useCallback((info: ContactInfo) => {
    setRevealedContactInfo(info);
  }, []);

  // Check if listing has any daily windows
  const hasDailyWindows = useMemo(() => {
    return windows.some(window => window.windowType === 'daily');
  }, [windows]);

  // Helper to check if a date has available daily windows
  const hasWindowOnDate = useCallback((date: Date): boolean => {
    const targetDate = new Date(date);
    targetDate.setHours(0, 0, 0, 0);
    const dayOfWeek = targetDate.getDay();
    
    return windows.some(window => {
      if (window.windowType !== 'daily') return false;
      
      // Check for non-repeating window with specific date
      if (!window.isRepeating && window.specificDate) {
        const specificDate = dateOnlyFieldToLocalMidnight(window.specificDate);
        return specificDate.getTime() === targetDate.getTime();
      }
      
      // Check for repeating window
      if (window.isRepeating && window.repeatDaysOfWeek) {
        // Check if this day of week is included
        if (!window.repeatDaysOfWeek.includes(dayOfWeek)) {
          return false;
        }
        
        // Check date range
        if (window.repeatStartDate) {
          const startDate = dateOnlyFieldToLocalMidnight(window.repeatStartDate);
          if (targetDate < startDate) return false;
        }
        
        if (window.repeatEndDate) {
          const endDate = dateOnlyFieldToLocalMidnight(window.repeatEndDate);
          if (targetDate > endDate) return false;
        }
        
        // Check exceptions
        if (window.repeatExceptions && window.repeatExceptions.length > 0) {
          const isException = window.repeatExceptions.some(exceptionDateStr => {
            const exceptionDate = dateOnlyFieldToLocalMidnight(exceptionDateStr);
            return exceptionDate.getTime() === targetDate.getTime();
          });
          if (isException) return false;
        }
        
        return true;
      }
      
      return false;
    });
  }, [windows]);

  const fetchListingData = useCallback(async () => {
    try {
      const [listingRes, windowsRes] = await Promise.all([
        api.get<{ listing: Listing }>(`/listings/name/${listingName}`),
        api.get<{ windows: RentalWindow[] }>(`/windows/listing/name/${listingName}`),
      ]);
      
      setListing(listingRes.listing);
      setWindows(windowsRes.windows);
      
      // Initialize renter details with empty strings
      const initialDetails: Record<string, string> = {};
      listingRes.listing.renterDetailRequirements.forEach((req) => {
        initialDetails[req.fieldName] = '';
      });
      setRenterDetails(initialDetails);

      // Auto-select today's date if listing has daily windows AND showCalendar is true
      const hasDailyWindow = windowsRes.windows.some(w => w.windowType === 'daily');
      if (hasDailyWindow && listingRes.listing.showCalendar !== false) {
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        setSelectedDate(today);
        // Fetch slots will be called by the useEffect watching selectedDate
      } else {
        // Nothing will pick a date for us: either the calendar is hidden, or the
        // listing only has weekly/monthly windows and so has no day to pick. Both
        // cases browse chronologically, and a weekly-only listing shows no
        // availability at all if we skip this.
        fetchChronologicalSlots(0);
      }
    } catch {
      toast.error('Failed to load listing');
    } finally {
      setIsLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [listingName]);

  // Fetch slots for a specific date
  const fetchSlotsForDate = useCallback(async (date: Date) => {
    setIsLoadingDateSlots(true);
    try {
      // Format date as YYYY-MM-DD
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      const dateStr = `${year}-${month}-${day}`;
      
      const slotsRes = await api.get<{ slots: TimeSlot[]; count: number }>(
        `/slots/listing/name/${listingName}/date/${dateStr}`
      );
      
      setSlots(slotsRes.slots);
    } catch {
      toast.error('Failed to load slots for selected date');
      setSlots([]);
    } finally {
      setIsLoadingDateSlots(false);
    }
  }, [listingName]);

  // Fetch slots chronologically (for when calendar is hidden)
  const fetchChronologicalSlots = useCallback(async (offset: number = 0) => {
    if (offset === 0) {
      setIsLoadingDateSlots(true);
    } else {
      setIsLoadingMoreSlots(true);
    }
    
    try {
      const slotsRes = await api.get<{ slots: TimeSlot[]; count: number }>(
        `/slots/listing/name/${listingName}?offset=${offset}&limit=12`
      );
      
      if (offset === 0) {
        setSlots(slotsRes.slots);
      } else {
        setSlots(prev => [...prev, ...slotsRes.slots]);
      }
      setHasMoreSlots(slotsRes.count === 12);
    } catch {
      toast.error('Failed to load slots');
      if (offset === 0) {
        setSlots([]);
      }
    } finally {
      setIsLoadingDateSlots(false);
      setIsLoadingMoreSlots(false);
    }
  }, [listingName]);

  // Load more slots (for pagination when calendar is hidden)
  const loadMoreSlots = useCallback(() => {
    if (!isLoadingMoreSlots && hasMoreSlots) {
      fetchChronologicalSlots(slots.length);
    }
  }, [isLoadingMoreSlots, hasMoreSlots, slots.length, fetchChronologicalSlots]);

  // Create unique key for slot
  const getSlotKey = (slot: TimeSlot): string => {
    return `${slot.windowId}-${slot.startDateTime}`;
  };

  // Toggle slot selection
  const toggleSlotSelection = useCallback((slot: TimeSlot) => {
    const key = getSlotKey(slot);
    setSelectedSlots(prev => {
      const newSet = new Set(prev);
      if (newSet.has(key)) {
        newSet.delete(key);
      } else {
        newSet.add(key);
      }
      return newSet;
    });
  }, []);

  // Clear all selected slots
  const clearAllSelections = useCallback(() => {
    setSelectedSlots(new Set());
  }, []);

  // Get selected slot objects
  const getSelectedSlotObjects = useMemo(() => {
    return slots.filter(slot => selectedSlots.has(getSlotKey(slot)));
  }, [slots, selectedSlots]);

  // Calculate total price
  const totalPrice = useMemo(() => {
    return getSelectedSlotObjects.reduce((sum, slot) => sum + slot.price, 0);
  }, [getSelectedSlotObjects]);

  // Handle calendar date selection
  const handleDateSelect = useCallback((date: Date) => {
    // Warn if there are selected slots
    if (selectedSlots.size > 0) {
      if (!window.confirm(`You have ${selectedSlots.size} slot(s) selected. Changing the date will clear your selections. Continue?`)) {
        return;
      }
      clearAllSelections();
    }
    setSelectedDate(date);
    fetchSlotsForDate(date);
  }, [fetchSlotsForDate, selectedSlots.size, clearAllSelections]);

  // Handle clearing date selection
  const handleClearDate = useCallback(() => {
    // Warn if there are selected slots
    if (selectedSlots.size > 0) {
      if (!window.confirm(`You have ${selectedSlots.size} slot(s) selected. Clearing the date will remove your selections. Continue?`)) {
        return;
      }
      clearAllSelections();
    }
    setSelectedDate(null);
    setSlots([]);
  }, [selectedSlots.size, clearAllSelections]);

  // Grid navigation handlers
  const handlePrevDay = useCallback(() => {
    if (!selectedDate) return;
    const prevDay = new Date(selectedDate);
    prevDay.setDate(prevDay.getDate() - 1);
    handleDateSelect(prevDay);
  }, [selectedDate, handleDateSelect]);

  const handleNextDay = useCallback(() => {
    if (!selectedDate) return;
    const nextDay = new Date(selectedDate);
    nextDay.setDate(nextDay.getDate() + 1);
    handleDateSelect(nextDay);
  }, [selectedDate, handleDateSelect]);

  useEffect(() => {
    fetchListingData();
  }, [fetchListingData]);

  // Initialize view from listing preference
  useEffect(() => {
    if (listing?.preferredSlotView) {
      setView(listing.preferredSlotView);
    }
  }, [listing?.preferredSlotView]);

  // Auto-fetch slots when date changes
  useEffect(() => {
    if (selectedDate && hasDailyWindows) {
      fetchSlotsForDate(selectedDate);
    }
  }, [selectedDate, hasDailyWindows, fetchSlotsForDate]);

  const handleAddSelectedToCart = async () => {
    if (getSelectedSlotObjects.length === 0) {
      toast.error('Please select at least one time slot');
      return;
    }

    // Validate required fields
    const missingFields = listing?.renterDetailRequirements
      .filter((req) => req.required && !renterDetails[req.fieldName]?.trim())
      .map((req) => req.fieldName);

    if (missingFields && missingFields.length > 0) {
      toast.error(`Please fill in: ${missingFields.join(', ')}`);
      return;
    }

    setIsBooking(true);
    try {
      // Add all selected slots to cart
      for (const slot of getSelectedSlotObjects) {
        await addItem({
          listingId: listing!._id,
          windowId: slot.windowId,
          startDateTime: new Date(slot.startDateTime),
          endDateTime: new Date(slot.endDateTime),
          price: slot.price,
          renterDetails,
        });
      }

      toast.success(`Added ${getSelectedSlotObjects.length} slot(s) to cart!`);
      clearAllSelections();
      navigate('/cart');
    } catch {
      toast.error('Failed to add to cart');
    } finally {
      setIsBooking(false);
    }
  };

  if (isLoading) {
    return <div className="text-center py-8">Loading...</div>;
  }

  if (!listing) {
    return <div className="text-center py-8">Listing not found</div>;
  }

  // Helper function to extract YouTube video ID
  const getYouTubeVideoId = (url: string): string | null => {
    const regExp = /^.*(youtu.be\/|v\/|u\/\w\/|embed\/|watch\?v=|&v=)([^#&?]*).*/;
    const match = url.match(regExp);
    return match && match[2].length === 11 ? match[2] : null;
  };

  // Prepare lightbox slides
  const lightboxSlides = listing?.photos.map((photo) => ({
    src: `${s3BaseUrl}/${photo.s3Key}`,
  })) || [];

  // Calculate minimum price for SEO
  const minPrice = slots.length > 0 
    ? Math.min(...slots.map(slot => slot.price))
    : undefined;

  return (
    <>
      <ListingSEO
        title={listing.title}
        description={listing.description}
        listingName={listing.name}
        photos={listing.photos}
        price={minPrice}
        address={listing.address}
        showAddress={listing.showAddress}
        s3BaseUrl={s3BaseUrl}
      />
      <div className="max-w-4xl mx-auto">
        {/* Listing Title */}
        <h1 className="text-3xl font-bold text-gray-900 mb-6">{listing.title}</h1>
        
      {/* Photo Gallery */}
      <div className="bg-white rounded-lg shadow-sm overflow-hidden mb-6">
        {listing.photos && listing.photos.length > 0 && (
          <div>
            {/* Primary Photo */}
            {listing.photos.length > 0 && (
              <div
                className="relative h-96 bg-gray-200 cursor-pointer overflow-hidden"
                onClick={() => {
                  setLightboxIndex(0);
                  setLightboxOpen(true);
                }}
              >
                <img
                  src={`${s3BaseUrl}/${listing.photos[0].s3Key}`}
                  alt={listing.title}
                  className="w-full h-full object-contain"
                />
              </div>
            )}
            
            {/* Thumbnail Grid */}
            {listing.photos.length > 1 && (
              <div className="grid grid-cols-4 gap-2 p-4">
                {listing.photos.slice(1, 5).map((photo, index) => (
                  <div
                    key={photo.id}
                    className="relative h-24 bg-gray-200 cursor-pointer overflow-hidden rounded"
                    onClick={() => {
                      setLightboxIndex(index + 1);
                      setLightboxOpen(true);
                    }}
                  >
                    <img
                      src={`${s3BaseUrl}/${photo.s3Key}`}
                      alt={`${listing.title} ${index + 2}`}
                      className="w-full h-full object-contain"
                    />
                    {index === 3 && listing.photos.length > 5 && (
                      <div className="absolute inset-0 bg-black bg-opacity-60 flex items-center justify-center">
                        <span className="text-white text-lg font-semibold">
                          +{listing.photos.length - 5} more
                        </span>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Lightbox */}
      <Lightbox
        open={lightboxOpen}
        close={() => setLightboxOpen(false)}
        index={lightboxIndex}
        slides={lightboxSlides}
      />

      {/* Conditionally order Description and Availability sections */}
      {listing.showAvailabilityAboveDescription ? (
        <>
          <AvailabilitySection
            listing={listing}
            windows={windows}
            slots={slots}
            selectedDate={selectedDate}
            view={view}
            hasDailyWindows={hasDailyWindows}
            selectedSlots={selectedSlots}
            isLoadingDateSlots={isLoadingDateSlots}
            isLoadingMoreSlots={isLoadingMoreSlots}
            hasMoreSlots={hasMoreSlots}
            hasWindowOnDate={hasWindowOnDate}
            handleDateSelect={handleDateSelect}
            handleClearDate={handleClearDate}
            setView={setView}
            toggleSlotSelection={toggleSlotSelection}
            handlePrevDay={handlePrevDay}
            handleNextDay={handleNextDay}
            loadMoreSlots={loadMoreSlots}
            getSlotKey={getSlotKey}
          />
          <BookingDetailsSection
            listing={listing}
            selectedSlots={selectedSlots}
            getSelectedSlotObjects={getSelectedSlotObjects}
            totalPrice={totalPrice}
            renterDetails={renterDetails}
            setRenterDetails={setRenterDetails}
            handleAddSelectedToCart={handleAddSelectedToCart}
            isBooking={isBooking}
            clearAllSelections={clearAllSelections}
          />
          <div className="bg-white rounded-lg shadow-sm overflow-hidden mb-6">
            <DescriptionSection
              listing={listing}
              revealedContactInfo={revealedContactInfo}
              handleContactReveal={handleContactReveal}
            getYouTubeVideoId={getYouTubeVideoId}
            />
          </div>
        </>
      ) : (
        <>
          <div className="bg-white rounded-lg shadow-sm overflow-hidden mb-6">
            <DescriptionSection
              listing={listing}
              revealedContactInfo={revealedContactInfo}
              handleContactReveal={handleContactReveal}
            getYouTubeVideoId={getYouTubeVideoId}
            />
          </div>
          <AvailabilitySection
            listing={listing}
            windows={windows}
            slots={slots}
            selectedDate={selectedDate}
            view={view}
            hasDailyWindows={hasDailyWindows}
            selectedSlots={selectedSlots}
            isLoadingDateSlots={isLoadingDateSlots}
            isLoadingMoreSlots={isLoadingMoreSlots}
            hasMoreSlots={hasMoreSlots}
            hasWindowOnDate={hasWindowOnDate}
            handleDateSelect={handleDateSelect}
            handleClearDate={handleClearDate}
            setView={setView}
            toggleSlotSelection={toggleSlotSelection}
            handlePrevDay={handlePrevDay}
            handleNextDay={handleNextDay}
            loadMoreSlots={loadMoreSlots}
            getSlotKey={getSlotKey}
          />
          <BookingDetailsSection
            listing={listing}
            selectedSlots={selectedSlots}
            getSelectedSlotObjects={getSelectedSlotObjects}
            totalPrice={totalPrice}
            renterDetails={renterDetails}
            setRenterDetails={setRenterDetails}
            handleAddSelectedToCart={handleAddSelectedToCart}
            isBooking={isBooking}
            clearAllSelections={clearAllSelections}
          />
        </>
      )}
    </div>
    </>
  );
};

export default PublicListingPage;
